/**
 * LO QUE DICE LA PANTALLA DE DANOS SEGUN LA REGLA QUE LA API USA (D-39, nexo-api 302).
 *
 * Antes de la regla unica, el ranking tenia tres capas: quien CONTO, quien tenia la SESION
 * abierta en el Desktop y el historico sin usuario. Desde la 302 solo queda la primera: lo que
 * no calza con ninguna diferencia del conteo de ese dia no se le carga a nadie (EC septiembre:
 * 20 danos, 1,1 millones, #17339). La API conserva los mismos campos, con la capa de sesion
 * siempre en 0.
 *
 * Los textos de la vista hablaban de las tres capas y de "las tablas de abajo". Con la regla
 * nueva eso miente: dice "0 solo se saben por la sesion abierta" y manda a mirar tablas que ya
 * no existen. Aqui el texto se decide por la FORMA de la respuesta, no por la fecha del deploy:
 * si hay capa de sesion, se habla de ella; si no, de lo que no calza. Asi no miente con
 * ninguna de las dos APIs.
 *
 * D-41 (nexo-api 305, #17408): los DANOS ya no son de quien conto: se reparten entre quienes
 * trabajaron ese dia operativo en el area (quien cuenta solo firma). Las entradas de ajuste
 * siguen a quien conto. Lo que no tiene area o no tuvo a nadie marcado queda sin repartir.
 *
 * Sin DOM: lo prueba scripts/check-danos-textos.mjs.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const CAPAS_CON_TABLA_DEBAJO = ['usuario', 'propietario'];

/** Los tres contadores de la respuesta, y si hay filas fuera de la tabla de quien conto. */
export function capasDeDanos(d) {
  const items = Array.isArray(d?.items) ? d.items : [];
  const capa = (f) => String(f?.capa ?? '').toLowerCase();
  const conteo = num(d?.atribuidoPorConteo);
  const usuario = num(d?.atribuidoPorUsuario);
  const sin = num(d?.sinIdentificar);
  return {
    conteo,
    usuario,
    sin,
    total: conteo + usuario + sin,
    fiabilidad: num(d?.fiabilidadPct),
    // Regla vieja: hay ajustes que solo se saben por la sesion abierta.
    hayCapaDeSesion: usuario > 0 || items.some((f) => CAPAS_CON_TABLA_DEBAJO.includes(capa(f))),
    hayTablasDebajo: items.some((f) => capa(f) !== 'conteo')
  };
}

/**
 * El contexto y la accion del bloque de fiabilidad, en texto plano (la vista escapa).
 * @returns {{contexto: string, accion: string}}
 */
export function textoDeFiabilidad(d) {
  const c = capasDeDanos(d);
  const n = (v) => Number(v).toLocaleString('es-CO');
  const contexto = c.hayCapaDeSesion
    ? `De ${n(c.total)} movimientos, ${n(c.conteo)} se pudieron amarrar a quien conto. `
      + `Otros ${n(c.usuario)} solo se saben por la sesion abierta`
      + (c.sin ? `, y ${n(c.sin)} no se saben.` : '.')
    // D-41 (nexo-api 305): los DANOS se reparten entre quienes trabajaron ese dia en el area; las
    // entradas de ajuste siguen a quien conto. Lo que no tiene area o nadie marco ese dia: sin repartir.
    : `De ${n(c.total)} ajustes, ${n(c.conteo)} tienen dueno: cada dano se reparte entre quienes trabajaron ese dia en el area, y cada entrada de ajuste va a quien conto. `
      + (c.sin
        ? `Los otros ${n(c.sin)} quedan sin repartir (nadie del area marco ese dia, o no se sabe el area) y no se le cargan a nadie.`
        : 'Todos quedaron repartidos.');

  let accion;
  if (c.fiabilidad >= 90) accion = 'Los numeros de la primera tabla se pueden usar para hablar con la gente.';
  else if (c.hayTablasDebajo) {
    accion = c.fiabilidad >= 60
      ? 'Habla solo sobre la primera tabla. Las de abajo no dicen quien lo hizo.'
      : 'NO uses estos numeros para evaluar a nadie todavia: la mayoria no se puede amarrar a quien conto.';
  } else {
    accion = c.fiabilidad >= 60
      ? 'La tabla dice la parte de cada persona; lo que quedo sin repartir no esta en ella ni se le carga a nadie.'
      : 'NO uses estos numeros para evaluar a nadie todavia: la mayoria de los ajustes quedo sin repartir.';
  }
  return { contexto, accion };
}

/**
 * La advertencia de la API, solo si agrega algo. Con la 302 dice lo mismo que el contexto de
 * arriba (cuantos se atribuyen y que lo demas no se le carga a nadie): pintarla repetia el dato.
 */
export function advertenciaAMostrar(d) {
  const texto = String(d?.advertencia ?? '').trim();
  return capasDeDanos(d).hayCapaDeSesion ? texto : '';
}
